import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    HostListener,
    Inject,
    OnInit,
    effect,
    inject,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormGroup,
    Validators,
} from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { NgxSpinnerService } from 'ngx-spinner';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '@app/core/services/toast.service';
import { DiscrepancyChoices } from '@app/shared/models/business-model/discrepancy-choices';
import { StorageService } from '@app/core/services/client-services/storage-service/storage.service';
import { SessionService } from '@app/core/services/client-services/session-service/session.service';
import { lastValueFrom } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
    ArticleDiscrepanciesResponse,
    ArticleDiscrepancyResponse,
    ArticleResponseType,
    AsnMetaData,
    LoadCarrier,
    MetaDataEnum,
} from 'api/addons/models';
import { DiscrepanciesService } from 'api/addons/services/discrepancies.service';
import { BundleGroupsService } from 'api/addons-v3/services';
import { VirtualArticleDiscrepanciesResponse } from 'api/addons-v3/models';
import { DeliveryNoteWithDiscrepancies } from '@app/consignee/components/checkin-notes-articles/checkin-notes-articles.component';
import { findAsnMetaData } from '@app/shared/utils';
import {
    hasConsigneeArticleDiscrepancies,
    hasConsigneeShowItemQRCode,
    hasGeneralGUIFeatures,
} from '@app/core/stores/permissionsStore';
import {
    FeatureType,
    FeatureHintComponent,
} from '@app/shared/components/feature-hints/feature-hint/feature-hint.component';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { MaterialModule } from '@app/shared/material/material.module';
import { QrCodeComponent } from 'ng-qrcode';
import { NgxBarcode6 } from 'ngx-barcode6';
import { LoadCarrierFormComponent } from './load-carrier-form/load-carrier-form.component';

export interface Discrepancy {
    reason: string;
    number: number;
    unit: string;
    note: string;
}

export interface DiscrepancyDialogResult {
    hasDiscrepancies: boolean;
}

const CODE_TYPE_STORAGE_KEY = 'discrepancyDialogCodeType';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-articles-discrepancies-dialog',
    templateUrl: './articles-discrepancies-dialog.component.html',
    styleUrls: ['./articles-discrepancies-dialog.component.scss'],
    imports: [
        MaterialModule,
        TranslateModule,
        ReactiveFormsModule,
        QrCodeComponent,
        NgxBarcode6,
        FeatureHintComponent,
        LoadCarrierFormComponent,
    ],
})
export class ArticlesDiscrepanciesDialogComponent
    implements OnInit, AfterViewInit
{
    discrepanciesForm: UntypedFormGroup;
    formSaved: boolean = false;
    discrepancyChoices: DiscrepancyChoices;
    initialFormDiscrepancies: ArticleDiscrepancyResponse[] = [];
    deliveryNotes?: DeliveryNoteWithDiscrepancies[];
    isQRCode: boolean;

    deliveryNotesSignal = inject(MAT_DIALOG_DATA).deliveryNotesSignal;

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            organizationSiteKey: string;
            deliveryNoteKey: string;
            article: ArticleResponseType;
            deliveryNotesSignal: () => DeliveryNoteWithDiscrepancies[];
            /**
             * When set, the discrepancies belong to an aggregated virtual
             * article of that bundle group instead of a real article.
             */
            groupKey?: string;
        },
        public dialogRef: MatDialogRef<ArticlesDiscrepanciesDialogComponent>,
        private spinner: NgxSpinnerService,
        private translateService: TranslateService,
        private toastrService: ToastService,
        private formBuilder: UntypedFormBuilder,
        private storageService: StorageService,
        private discrepancyService: DiscrepanciesService,
        private bundleGroupsService: BundleGroupsService,
        private sessionService: SessionService,
        private cdr: ChangeDetectorRef
    ) {
        effect(() => {
            this.deliveryNotes = this.deliveryNotesSignal();
            this.onDeliveryNotesChange();
        });
        this.loadCodeTypePreference();
    }

    ngAfterViewInit(): void {
        // The dialog is created synchronously while the overlay is still being
        // change-detected, so async pipes (e.g. translate) in the header settle
        // from their initial value after the first check. Running an explicit
        // detection pass here stabilizes the view before Angular's dev-mode
        // verification and avoids NG0100 ExpressionChangedAfterItHasBeenChecked.
        this.cdr.detectChanges();
    }

    isDiscrepancyAutomatic(index: number) {
        return this.initialFormDiscrepancies[index]?.isAutomatic ?? false;
    }

    private onDeliveryNotesChange() {
        // Trigger any updates needed when deliveryNotes changes
        if (this.deliveryNotes) {
            this.countAdoptionLoadCarriersFromConsignee();
        }
    }

    async ngOnInit() {
        this.discrepanciesForm = this.formBuilder.group({});
        this.getAllDiscrepancyChoices();
        this.initForm();
        await this.requestArticleDiscrepancies();

        this.loadCodeTypePreference();
    }

    private loadCodeTypePreference(): void {
        const savedPreference = this.storageService.getItemAsObject(
            CODE_TYPE_STORAGE_KEY
        );
        if (savedPreference) {
            this.isQRCode = savedPreference === 'true';
        } else {
            this.isQRCode = true;
        }
    }

    private saveCodeTypePreference(): void {
        this.storageService.setItem(
            CODE_TYPE_STORAGE_KEY,
            this.isQRCode.toString()
        );
    }

    async requestArticleDiscrepancies() {
        if (!hasConsigneeArticleDiscrepancies()) {
            return;
        }
        const discrepancies = this.data.groupKey
            ? this.toArticleDiscrepancies(
                  await lastValueFrom(
                      this.bundleGroupsService.getBundleGroupVirtualArticleDiscrepancies(
                          {
                              organizationSiteKey:
                                  this.data.organizationSiteKey,
                              groupKey: this.data.groupKey,
                              virtualArticleKey: this.data.article._key,
                          }
                      )
                  )
              )
            : await lastValueFrom(
                  this.discrepancyService.getArticleDiscrepanciesForArticle({
                      organizationSiteKey: this.data.organizationSiteKey,
                      articleKey: this.data.article._key,
                      deliveryNoteKey: this.data.deliveryNoteKey,
                  })
              );

        if (discrepancies) {
            this.discrepanciesFormArray.clear();
            this.pushDiscrepanciesToForm(discrepancies);
            this.initialFormDiscrepancies = discrepancies;
        }

        // The form is mutated after an awaited HTTP call (outside any change
        // detection pass in this zoneless app), which flips `discrepanciesForm.invalid`
        // and `formSaved`. Render the settled state synchronously so a later
        // verification pass doesn't observe a mid-flight change (NG0100).
        this.cdr.detectChanges();
    }

    /**
     * Normalizes virtual-article discrepancies (which carry a
     * `virtualArticleKey`) onto the article discrepancy shape used by the form.
     */
    private toArticleDiscrepancies(
        discrepancies: VirtualArticleDiscrepanciesResponse
    ): ArticleDiscrepancyResponse[] {
        return (discrepancies ?? []).map(discrepancy => ({
            ...discrepancy,
            articleKey: discrepancy.virtualArticleKey,
        }));
    }

    pushDiscrepanciesToForm(data: ArticleDiscrepancyResponse[]): void {
        data.forEach(item => {
            const discrepancy = item;
            const obj: Discrepancy = {
                reason: discrepancy.reason,
                unit: discrepancy.unit,
                number: discrepancy.number,
                note: discrepancy.note,
            };
            this.discrepanciesFormArray.push(this.createNewDiscrepancy(obj));
        });
        this.formSaved = true;

        this.discrepanciesForm.valueChanges
            .pipe(untilDestroyed(this))
            .subscribe(() => {
                this.formSaved = false;
            });
    }

    addDiscrepancy() {
        this.formSaved = false;
        this.discrepanciesFormArray.push(this.createNewDiscrepancy());
    }

    closeDialog() {
        if (this.formSaved) {
            const result: DiscrepancyDialogResult = {
                hasDiscrepancies: this.discrepanciesFormArray.length > 0,
            };
            this.dialogRef.close(result);
        } else {
            this.dialogRef.close();
        }
    }

    private createNewDiscrepancy(obj?: Discrepancy): UntypedFormGroup {
        return this.formBuilder.group({
            reason: [obj?.reason || '', Validators.required],
            number: [
                obj?.number || '',
                [Validators.required, Validators.min(0)],
            ],
            unit: [obj?.unit || '', Validators.required],
            note: [obj?.note || ''],
        });
    }

    getAllDiscrepancyChoices(): void {
        this.discrepancyChoices =
            this.storageService.getItemAsObject('discrepancyChoices');
    }

    private initForm(): void {
        this.discrepanciesForm.addControl(
            'discrepancy',
            this.formBuilder.array([])
        );
    }

    get discrepanciesFormArray(): UntypedFormArray {
        return this.discrepanciesForm.get('discrepancy') as UntypedFormArray;
    }

    async saveDiscrepancies() {
        const payload = this.discrepanciesForm.value.discrepancy;
        await this.spinner.show();
        try {
            const result: ArticleDiscrepanciesResponse = this.data.groupKey
                ? this.toArticleDiscrepancies(
                      await lastValueFrom(
                          this.bundleGroupsService.setBundleGroupVirtualArticleDiscrepancies(
                              {
                                  organizationSiteKey:
                                      this.data.organizationSiteKey,
                                  groupKey: this.data.groupKey,
                                  virtualArticleKey: this.data.article._key,
                                  body: payload,
                              }
                          )
                      )
                  )
                : await lastValueFrom(
                      this.discrepancyService.putConsigneeArticleDiscrepancies({
                          organizationSiteKey: this.data.organizationSiteKey,
                          deliveryNoteKey: this.data.deliveryNoteKey,
                          articleKey: this.data.article._key,
                          body: payload,
                      })
                  );

            if (result) {
                this.initialFormDiscrepancies = result;
            }

            this.formSaved = true;
            this.toastrService.success(
                this.translateService.instant(
                    'INCOMING-GOODS.DISCREPANCIES.SAVING.SUCCESS'
                )
            );
        } catch (error: any | HttpErrorResponse) {
            if (error && error.error.length) {
                if (error.error[0].message.includes('>= 1')) {
                    this.toastrService.error(
                        this.translateService.instant(
                            'INCOMING-GOODS.DISCREPANCIES.SAVING.FAILED-COUNT-0'
                        )
                    );
                } else {
                    this.toastrService.error(
                        this.translateService.instant(
                            'INCOMING-GOODS.DISCREPANCIES.SAVING.FAILED'
                        )
                    );
                }
            } else {
                this.toastrService.error(
                    this.translateService.instant(
                        'INCOMING-GOODS.DISCREPANCIES.SAVING.FAILED'
                    )
                );
            }
        } finally {
            await this.spinner.hide();
        }
    }

    async removeDiscrepancy(index: number) {
        this.formSaved = false;
        this.discrepanciesFormArray.removeAt(index);

        const payload = this.discrepanciesForm.value.discrepancy;
        if (payload.length === this.initialFormDiscrepancies.length) {
            let isSaveNeeded = false;
            for (let i = 0; i < payload.length; i++) {
                if (
                    payload[i].reason !=
                        this.initialFormDiscrepancies[i].reason ||
                    payload[i].number !=
                        this.initialFormDiscrepancies[i].number ||
                    payload[i].unit != this.initialFormDiscrepancies[i].unit
                ) {
                    isSaveNeeded = true;
                    break;
                }
            }

            this.formSaved = !isSaveNeeded;
        }
    }

    countAdoptionLoadCarriersFromConsignee() {
        const loadCarrierCounts: { [carrierName: string]: number } = {};
        if (this.deliveryNotes) {
            this.deliveryNotes.forEach(note => {
                note.loadCarriersConsignee?.forEach((carrier: LoadCarrier) => {
                    if (carrier.operation === 'delivery') {
                        if (!loadCarrierCounts[carrier.name]) {
                            loadCarrierCounts[carrier.name] = 0;
                        }
                        loadCarrierCounts[carrier.name] += carrier.quantity;
                    }
                });
            });
        }
        return loadCarrierCounts;
    }

    toggleCodeType() {
        this.isQRCode = !this.isQRCode;
        this.saveCodeTypePreference();
    }

    getCodeType(): string {
        return this.isQRCode ? 'QR Code' : 'Bar Code';
    }

    getQRSize(): number {
        // Calculate size based on viewport height
        const viewportHeight = window.innerHeight;
        // Use 40% of viewport height as the maximum size
        return Math.min(Math.floor(viewportHeight * 0.4), 400);
    }

    @HostListener('window:resize')
    updateQRSize() {
        // This will trigger a re-render of the QR codes when window is resized
        this.getQRSize();
    }

    findAsnMetaData(): AsnMetaData | undefined {
        return findAsnMetaData(this.data.article?.metaData);
    }

    getItemCode() {
        const article = this.data.article;
        return (
            this.findAsnMetaData()?.item ||
            article?.edits?.customerArticleNumber?.value ||
            article?.customerArticleNumber ||
            undefined
        );
    }

    protected readonly hasConsigneeArticleDiscrepancies =
        hasConsigneeArticleDiscrepancies;
    protected readonly hasConsigneeShowItemQRCode = hasConsigneeShowItemQRCode;
    protected readonly FeatureType = FeatureType;
    protected readonly hasGeneralGUIFeatures = hasGeneralGUIFeatures;
}
